/*
 *                     NOTICE
 *            COPYRIGHT 2020 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { NestedError } from "abweb/dto/exception/NestedError";
import * as AbWebResources from "abweb/generated/locale/AbWebResources";
import { BrowserCheck, SupportedBrowser } from "abweb/service/utils/BrowserCheck";
import { LocaleUtils } from "abweb/utils/LocaleUtils";

/**
 * The 'fetch' api failed hard, and in such a way that a response is not available.
 * This is known to happen for at least the following cases:
 * <ol>
 *   <li>Server Unavailable: either an invalid url, or the server is down</li>
 *   <li>Can't follow redirects: if server returns with a redirect, and the Fetch call is not configured to
 *   follow redirects, then you'll get a FailedToFetch error.  This is rare these days as we default
 *   fetch invocations to follow redirects.</li>
 * </ol>
 *
 */
export class FailedToFetchError extends NestedError {
  // Fetch throws has a very general error when something goes wrong (like server unavailable,
  // or it can't follow a redirect).  This determines if the error is the 'raw fetch error'.
  // The Fetch-thrown error is very generic, probably because they don't want to indicate
  // exactly what went wrong due to security concerns.
  // We have a test (EchoXSpec) which will fail if the the browsers change the exception that they throw
  // (either localize msg, or different types of exceptions altogether) - this is useful info if it conveys
  // meaningful new information (finer-grained exceptiosn) that we can leverage.
  static isFailedToFetchError(rawUntypedErr) {
    return rawUntypedErr?.message === (BrowserCheck.browser === SupportedBrowser.FIREFOX ? "NetworkError when attempting to fetch resource." : "Failed to fetch"); // Chrome and Edge
  }
  constructor(causedBy) {
    const failedToFetchMsg = LocaleUtils.safeResourceLookup(AbWebResources.fetchError_failedToFetch, "Failed to fetch");
    super(failedToFetchMsg, causedBy);
    // Defensive: if any of the browsers starts reporting more fine-grained fetch errors, we'd like to know.
    // Commenting out as EchoXSpec."verify server not reachable by invoking rpc against non-existing server"
    // also verifies, with no production-time overhead impact.
    // if (!FailedToFetchError.isFailedToFetchError(causedBy))
    // {
    //   LOG.warn("Unexpected causedBy error: " + ErrorUtils.getErrorMsgFromObj(causedBy))
    // }
  }
}
//# sourceMappingURL=FailedToFetchError.js.map
